import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowLeft } from 'lucide-react';

const TermsConditions = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white pt-24 pb-20">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          <Button 
            onClick={() => navigate('/')}
            variant="outline"
            className="mb-6 border-blue-600 text-blue-600 hover:bg-blue-50"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>

          <h1 className="text-5xl font-bold text-gray-900 mb-12">
            Terms & <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Conditions</span>
          </h1>

          <div className="prose prose-lg max-w-none space-y-8">
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">1. Agreement to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                By accessing and using the Code Commerce website (codecommerce.co) and our services, you agree to be bound by these Terms and Conditions. If you disagree with any part of these terms, you may not access our website or use our services.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">2. Services Offered</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Code Commerce provides the following professional services:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Web Development and Design</li>
                <li>UI/UX Design Services</li>
                <li>Blockchain Development</li>
                <li>E-commerce Development</li>
                <li>Custom Software Development</li>
                <li>Mobile App Development (iOS and Android)</li>
                <li>3D Animation and Visual Design</li>
                <li>Cloud Solutions and Infrastructure</li>
                <li>Digital Marketing Solutions</li>
                <li>Creative Copywriting and Content Creation</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                Specific terms for each service will be outlined in individual service agreements.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">3. Service Agreements</h2>
              <h3 className="text-2xl font-semibold text-gray-800 mb-3">3.1 Quotes and Proposals</h3>
              <p className="text-gray-700 leading-relaxed">
                All quotes and proposals are valid for 30 days from the date of issuance unless otherwise specified. Pricing may be subject to change based on project scope modifications.
              </p>

              <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">3.2 Project Scope</h3>
              <p className="text-gray-700 leading-relaxed">
                Project deliverables, timelines, and specifications will be detailed in a separate Statement of Work (SOW) or project agreement. Any changes to the agreed scope may result in additional costs and timeline adjustments.
              </p>

              <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">3.3 Client Responsibilities</h3>
              <p className="text-gray-700 leading-relaxed">
                Clients are responsible for providing timely feedback, necessary materials, access to systems, and approvals required for project completion.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">4. Payment Terms</h2>
              <h3 className="text-2xl font-semibold text-gray-800 mb-3">4.1 Pricing and Fees</h3>
              <p className="text-gray-700 leading-relaxed">
                All prices are quoted in US Dollars (USD) and are exclusive of applicable taxes unless otherwise stated. Payment terms will be specified in individual service agreements.
              </p>

              <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">4.2 Payment Schedule</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Standard payment terms include:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>50% deposit upon project commencement</li>
                <li>25% upon project milestone completion</li>
                <li>25% upon final delivery and approval</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                Custom payment schedules may be arranged for larger projects.
              </p>

              <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">4.3 Late Payments</h3>
              <p className="text-gray-700 leading-relaxed">
                Late payments may incur interest charges of 1.5% per month or the maximum rate permitted by law, whichever is lower. We reserve the right to suspend work on projects with overdue payments.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">5. Intellectual Property Rights</h2>
              <h3 className="text-2xl font-semibold text-gray-800 mb-3">5.1 Client Ownership</h3>
              <p className="text-gray-700 leading-relaxed">
                Upon full payment, clients own the final deliverables specifically created for their project, including custom code, designs, and content.
              </p>

              <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">5.2 Code Commerce Rights</h3>
              <p className="text-gray-700 leading-relaxed">
                We retain ownership of our pre-existing tools, frameworks, libraries, and methodologies. We reserve the right to showcase completed projects in our portfolio unless a non-disclosure agreement specifies otherwise.
              </p>

              <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">5.3 Third-Party Components</h3>
              <p className="text-gray-700 leading-relaxed">
                Projects may incorporate third-party software, plugins, or services subject to their own licenses and terms.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">6. Warranties and Disclaimers</h2>
              <h3 className="text-2xl font-semibold text-gray-800 mb-3">6.1 Service Warranty</h3>
              <p className="text-gray-700 leading-relaxed">
                We warrant that our services will be performed with professional skill and care. Bug fixes and corrections will be provided for 30 days after project delivery at no additional cost.
              </p>

              <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">6.2 Disclaimer</h3>
              <p className="text-gray-700 leading-relaxed">
                EXCEPT AS EXPRESSLY PROVIDED, OUR SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DO NOT GUARANTEE SPECIFIC RESULTS OR BUSINESS OUTCOMES.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">7. Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed">
                To the maximum extent permitted by law, Code Commerce shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including lost profits, data loss, or business interruption. Our total liability shall not exceed the amount paid by the client for the specific service giving rise to the claim.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">8. Confidentiality</h2>
              <p className="text-gray-700 leading-relaxed">
                Both parties agree to keep confidential information disclosed during the business relationship private and secure. This obligation survives the termination of any agreement between the parties.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">9. Project Cancellation and Refunds</h2>
              <h3 className="text-2xl font-semibold text-gray-800 mb-3">9.1 Client Cancellation</h3>
              <p className="text-gray-700 leading-relaxed">
                Clients may cancel projects with written notice. Deposits are non-refundable. Clients will be invoiced for work completed up to the cancellation date.
              </p>

              <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">9.2 Our Cancellation Rights</h3>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to terminate a project if the client breaches these terms, fails to make timely payments, or if continued work is not feasible.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">10. Website Use</h2>
              <h3 className="text-2xl font-semibold text-gray-800 mb-3">10.1 Acceptable Use</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                You agree not to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Use our website for any unlawful purpose</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Interfere with the proper functioning of our website</li>
                <li>Transmit viruses, malware, or harmful code</li>
                <li>Harvest or collect user information without consent</li>
              </ul>

              <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">10.2 Content Accuracy</h3>
              <p className="text-gray-700 leading-relaxed">
                While we strive to keep information accurate and current, we make no representations about the completeness or accuracy of website content.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">11. Dispute Resolution</h2>
              <h3 className="text-2xl font-semibold text-gray-800 mb-3">11.1 Governing Law</h3>
              <p className="text-gray-700 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the State of California, USA, without regard to its conflict of law provisions.
              </p>

              <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">11.2 Arbitration</h3>
              <p className="text-gray-700 leading-relaxed">
                Any disputes arising from these Terms or our services shall be resolved through binding arbitration in Los Angeles, California, rather than in court, except that you may assert claims in small claims court if they qualify.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">12. Modifications to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to our website. Your continued use of our services after changes constitutes acceptance of the modified Terms.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">13. Contact Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                For questions about these Terms and Conditions, please contact us:
              </p>
              <div className="bg-blue-50 p-6 rounded-lg">
                <p className="text-gray-900 font-semibold">Code Commerce</p>
                <p className="text-gray-700">12100 Wilshire Blvd, Los Angeles, CA 90025, USA</p>
                <p className="text-gray-700">Email: info@codecommerce.co</p>
                <p className="text-gray-700">Phone: +1 (833) 400 9440</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsConditions;